import { ComputedRef, Ref, computed, ref, unref, watch } from 'vue';

import { AttachmentsApi } from 'SharedDir/backend/api';
import { ATTACHMENT_ENDPOINTS } from 'SharedDir/backend/endpoints/attachments';
import { useMyCertificatesAccess } from 'SharedDir/common/composables/useMyCertificatesAccess';
import { usePermissions } from 'SharedDir/common/composables/usePermissions';
import { makeRequest } from 'SharedDir/common/composables/useRequests';
import { SignerInfo } from 'SharedDir/declarations/crypto-pro';
import { SignatureData } from 'SharedDir/declarations/files';
import { IEndpointData } from 'SharedDir/declarations/shared';

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

export type FieldFileSignedPayload = {
  data: SignatureData;
  done: () => void;
  fail: () => void;
};

export type FieldFileDocumentInfo = {
  name: string;
  endpoint: IEndpointData;
  needSign: boolean;
  id: string;
};

/**
 * ЭП для FileField / FileSetField.
 * FileFull без signature — при появлении fileId тянем GET /signature (signing.export).
 * Права signing.* — здесь; в SignedDocument проверки закомментированы (экспорт заявления).
 * View не блокирует подпись: кабинет сертификатов + signing.sign. PKCS#7 читается по signing.export.
 */
export function useFieldFileSigning(params: {
  fileId: MaybeRef<string | null | undefined>;
  fileName: MaybeRef<string | null | undefined>;
}) {
  const { showMyCertificates } = useMyCertificatesAccess();
  const { can, permissionSet } = usePermissions();

  const signature = ref<string | null>(null);
  const signers = ref<Omit<SignerInfo, 'thumbprint'>[]>([]);
  const loading = ref(false);

  const currentFileId = computed(() => unref(params.fileId) || '');
  const currentFileName = computed(() => unref(params.fileName) || '');

  // View поля не даёт менять файл, но подпись — отдельное действие: кабинет сертификатов + signing.sign.
  const canSign = computed(
    () => showMyCertificates.value && can(permissionSet.signing.sign)
  );

  // Просмотр сохранённой подписи не равен кабинету сертификатов: бэк пускает по signing.export.
  const canExportSignature = computed(() => can(permissionSet.signing.export));

  const showSigningUi = computed(
    () => Boolean(currentFileId.value) && (canSign.value || canExportSignature.value)
  );

  const allowSigning = computed(() => showSigningUi.value && canSign.value);

  const isSigned = computed(() => Boolean(signature.value));

  const documentInfo = computed<FieldFileDocumentInfo>(() => ({
    name: currentFileName.value,
    endpoint: ATTACHMENT_ENDPOINTS.GET(currentFileId.value),
    needSign: false,
    id: currentFileId.value,
  }));

  async function loadFileSignatureState() {
    signature.value = null;
    signers.value = [];

    // без export сервер не отдаёт PKCS#7 — только «Подписать» в этой сессии
    if (!currentFileId.value || !canExportSignature.value) {
      return;
    }

    loading.value = true;
    try {
      signature.value = await AttachmentsApi.getSignatureContent(currentFileId.value);
    } catch {
      signature.value = null;
    } finally {
      loading.value = false;
    }
  }

  watch(
    [currentFileId, canExportSignature],
    async () => {
      await loadFileSignatureState();
    },
    { immediate: true }
  );

  async function saveSignature({ data, done, fail }: FieldFileSignedPayload) {
    try {
      await makeRequest(() => AttachmentsApi.sign(currentFileId.value, data));
      done();
    } catch {
      fail();
    }
  }

  return {
    signature,
    signers,
    loading,
    isSigned,
    showSigningUi,
    allowSigning,
    canExportSignature,
    documentInfo,
    saveSignature,
  };
}
